/**
 * A repeating tick that keeps time while the tab is in the background (AD-794 §7.1: the queue
 * heartbeat must survive the visitor switching tabs to register "while you wait").
 *
 * Why not a plain `setInterval`: a hidden tab's main-thread timers are throttled — to once a
 * second at first and, after a few minutes hidden, to roughly once a MINUTE (Chrome's intensive
 * throttling). The server drops a queue entry after three missed beats, so a visitor who
 * registers in another tab for five minutes would silently lose their place. Two defences, both
 * cheap:
 *
 *   1. The tick is driven from a tiny dedicated Worker when the page can create one: worker
 *      timers are not subject to the main thread's intensive throttling, and a `message` event is
 *      not a timer, so the main thread receives it promptly even while hidden.
 *   2. Whatever drives it, returning to the tab fires an immediate tick (`visibilitychange`), so
 *      the position on screen is fresh the moment the visitor looks.
 *
 * Falls back to `setInterval` where a Worker cannot be made (a CSP without `worker-src blob:`,
 * an old browser, the Node test runner). The pure scheduling is injectable for tests.
 */

export interface Ticker {
  /** Start ticking every `intervalMs`; returns the stop function. */
  start(intervalMs: number, onTick: () => void): () => void;
}

/** A ticker over plain timers (the fallback, and what tests drive with fake timers). */
export function intervalTicker(
  setIntervalImpl: (fn: () => void, ms: number) => unknown = (fn, ms) => setInterval(fn, ms),
  clearIntervalImpl: (handle: unknown) => void = (h) =>
    clearInterval(h as ReturnType<typeof setInterval>),
): Ticker {
  return {
    start(intervalMs, onTick) {
      const handle = setIntervalImpl(onTick, intervalMs);
      return () => clearIntervalImpl(handle);
    },
  };
}

/** The worker body: post a tick every interval; nothing else. */
const WORKER_SOURCE =
  'let h=null;onmessage=(e)=>{if(h!==null)clearInterval(h);h=null;' +
  'if(e.data>0)h=setInterval(()=>postMessage(0),e.data);};';

/**
 * The browser ticker: Worker-driven where possible, plus an immediate tick when the tab becomes
 * visible again. Never throws — any failure to build the worker falls back to `intervalTicker`.
 */
export function browserTicker(): Ticker {
  return {
    start(intervalMs, onTick) {
      let stopDriver: () => void;
      try {
        const url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
        const worker = new Worker(url);
        URL.revokeObjectURL(url);
        worker.onmessage = () => onTick();
        worker.postMessage(intervalMs);
        stopDriver = () => {
          worker.postMessage(0);
          worker.terminate();
        };
      } catch {
        stopDriver = intervalTicker().start(intervalMs, onTick);
      }
      const onVisible = (): void => {
        if (document.visibilityState === 'visible') onTick();
      };
      document.addEventListener('visibilitychange', onVisible);
      return () => {
        document.removeEventListener('visibilitychange', onVisible);
        stopDriver();
      };
    },
  };
}
